<template>
  <div :class="['tags-content', { collapse: isCollapse }]">
    <el-dialog
      :visible.sync="dialogVisible"
      :show-close="false"
      :modal="false"
      :close-on-click-modal="false"
      @open="openDialog"
    >
      <el-checkbox-group v-model="checkedTags">
        <div
          :class="[
            'tags',
            { separator: tagGroupSeperator.includes(item.name) },
            { inactive: item.id === nonSimultaneousId }
          ]"
          v-for="(item, index) in tagItems"
          :key="index"
        >
          <span class="tagName">{{ item.name }}</span>
          <span class="description" v-if="isCollapse">{{ item.description }}</span>
          <el-checkbox
            :disabled="item.id === nonSimultaneousId"
            :label="item.id"
            :key="item.id"
          ></el-checkbox>
        </div>
        <div class="footer">
          <span @click="apply" :class="{ disabled: disabledApply }">APPLY</span>
          <span @click="reset">CANCEL</span>
        </div>
      </el-checkbox-group>
      <div :class="['btn-arrow', { collapse: isCollapse }]" @click="collapseDialog">
        <img />
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex';
import { debounce } from '@/utils/common';
import { getApiMethod } from '@/modules/apiSelector'

export default {
  props: {
    chatMessage: {
      type: String
    }
  },
  data() {
    return {
      dialogVisible: false,
      isCollapse: false,
      checkedTags: [],
      selectedWordRange: null,
      selectedWords: null,
      qlTag: null,
      selectedElements: null,
      tagGroupSeperator: ['#Pronunciation', '#Original']
    };
  },
  mounted() {
    this.initGetTrainerTagOptionList();
    
    this.quill.root.addEventListener('keyup', debounce(this.setFormatPerWord, 50));

    this.qlTag = document.querySelector('.ql-editor');
    this.qlTag.addEventListener('dblclick', this.showTagPopup);
    this.qlTag.addEventListener('mousedown', this.addTextBgSelection);
  },
  computed: {
    ...mapState('trainerTag', ['tagItems', 'trainerTags', 'editTag']),
    ...mapState('socketio', ['socket', 'connected']),
    ...mapState('layout', ['layoutStyle']),
    ...mapGetters(['isStudent', 'isWaitingForLesson', 'isLessonEnded', 'isZipanService']),

    quill() {
      return this.$parent.$refs.editor.quill;
    },

    nonSimultaneousId() {
      const refinedId = 5;
      const originalId = 4;

      return this.checkedTags.includes(refinedId)
        ? originalId
        : this.checkedTags.includes(originalId)
        ? refinedId
        : null;
    },

    tags() {
      return this.tagItems
        .filter(tags => this.checkedTags.includes(tags.id))
        .map(item => {
          return {
            name: item.name,
            vocabulary_tag_id: item.id
          };
        });
    },

    disabledApply() {
      return !this.checkedTags.length && !this.editTag;
    }
  },
  watch: {
    chatMessage(val) {
      if (!val) {
        this.reset();
      }
    },
    editTag(val) {
      if (val) {
        this.checkedTags = val.tags.map(tag => parseInt(tag.vocabulary_tag_id));
        this.dialogVisible = true;
      }
    }
  },
  methods: {
    ...mapMutations('trainerTag', ['addTrainerTags', 'setEditTag', 'setTrainerTags']),
    ...mapMutations('socketio', ['updateTrainerTag']),
    ...mapActions('trainerTag', ['saveTrainerTag']),

    setFormatPerWord(evt) {
      if (this.isFormatShortcutKeys(evt) || this.isLessonEnded || this.isWaitingForLesson) {
        return;
      }

      let index = 0;
      let vm = this;
      const textInput = this.quill.getText();

      // split text per space
      textInput.split(/\s/).map((word, key) => {
        const format = this.quill.getFormat(index, word.length);
        if (word.length && !format['phrase-noneditable']) {
          vm.quill.formatText(index, word.length + 1, 'ql-tag', key + 1);
        }
        index = index + word.length + 1;
      });

      this.removeChangedHighlightedText();
      this.hideEmptySpan();
    },

    isFormatShortcutKeys(evt) {
      if (!evt) {
        return;
      }
      evt.preventDefault();
      return (evt.key !== 'v' && evt.ctrlKey === true) || evt.key === 'Tab';
    },

    // remove highlighted format on tagged words that is changed
    removeChangedHighlightedText() {
      const highlight = document.querySelectorAll('.ql-editor a[class*="ql-tag-"]');
      if (highlight) {
        highlight.forEach(word => {
          const exist = this.trainerTags.find(tag => {
            const isMultipleWordTagExist = this.quill.getText().includes(tag.word.trimEnd());
            return tag.word.includes(word.innerText.trim()) && isMultipleWordTagExist && tag.tags.length;
          });
          if (!exist) {
            word.classList.remove('highlight');
          } else if (exist && word.tagName === 'A') {
            word.classList.add('highlight');
          }
        });
      }
    },

    // empty span is generated after the a[highlight] element
    hideEmptySpan() {
      let qlTag = document.querySelectorAll('[class*="ql-tag-"]');
      if (qlTag) {
        qlTag.forEach((word, key) => {
          const emptyString = !word.innerText || !word.innerText.trim().length;
          if (emptyString && qlTag.length - 1 > key) {
            word.style.display = 'none';
          }
        });
      }
    },

    showTagPopup(evt) {
      const range = this.quill.getSelection();
      if (this.isWaitingForLesson || this.isLessonEnded || !range) {
        return;
      }

      this.selectedWords = this.quill.getText(range);
      
      const format = this.quill.getFormat(range.index, range.length);
      if (format['phrase-noneditable'] || !this.selectedWords.trim()) {
        return;
      }

      if (range && range.length) {
        this.addActiveFormat(evt, range);

        this.dialogVisible = true;
        this.$nextTick(() => {
          this.$emit('setDialogPosition');
        });
      }
    },

    isMultipleSelection(evt) {
      return ['DIV', 'P'].includes(evt.srcElement.tagName);
    },

    addActiveFormat(evt, range) {
      this.qlTag.classList.remove('active-selection');

      // add active tag to the selected element
      if (!this.isMultipleSelection(evt) || !this.connected) {
        this.selectedWords = evt.srcElement.innerText;
        this.selectedElements = evt.srcElement;
        evt.srcElement.classList.add('active');
        return;
      }

      // check if word selection has space at the end
      const length = /\s+$/.test(this.selectedWords) ? range.length - 1 : range.length + 1;
      this.quill.formatText(range.index, length, { 'active-tag': true });
      this.selectedElements = { index: range.index, length };
    },

    addTextBgSelection(evt) {
      if (evt.detail === 2 && !this.isZipanService) {
        this.qlTag.classList.add('active-selection');
      }
    },

    openDialog() {
      this.itemId = null;
      this.quill.blur();
    },

    async apply() {
      if (this.disabledApply) {
        return;
      }

      this.addTrainerTags({
        word: this.selectedWords && this.selectedWords.trimEnd() || this.editTag.word,
        tags: this.tags.length ? this.tags : null,
        tag_ids: this.checkedTags.length ? this.checkedTags : null
      });

      this.quill.setSelection(this.quill.getLength(), 0);
      this.dialogVisible = false;

      if (this.editTag) {
        await this.emitEditTagOnSocket();
      }

      this.addHighlight();
      this.reset();
    },

    async emitEditTagOnSocket() {
      const message = { id: this.editTag.message_id, tagWordId: this.editTag.id, text: this.editTag.word };
      let data = await this.saveTrainerTag(message);
      if (data) {
        this.updateTrainerTag({ tags: this.tags, ...data[0].tagged_word });
      }
    },

    addHighlight() {
      if (!this.selectedElements) {
        return;
      }

      if (this.selectedElements.length) {
        this.quill.formatText(this.selectedElements.index, this.selectedElements.length, {
          'highlight-tag': true
        });
        return;
      }

      // replace element with link tag for highlight formatting
      let highlightElement = document.createElement('a');
      highlightElement.setAttribute('href', '#');
      this.selectedElements.classList.remove('active');
      highlightElement.setAttribute('class', this.selectedElements.className + ' highlight');
      highlightElement.innerHTML = this.selectedElements.innerHTML;
      this.selectedElements.replaceWith(highlightElement);
      this.$nextTick(() => {
        this.quill.setSelection(this.quill.getLength() - 1, 0);
      });
    },

    reset() {
      this.removeActiveFormatting();
      this.checkedTags = [];
      this.dialogVisible = false;
      this.selectedWords = null;
      this.setEditTag(null);
    },

    removeActiveFormatting() {
      this.quill.formatText(0, this.quill.getLength(), { 'active-tag': false });
      let activeWord = document.querySelectorAll('[class*="ql-tag-"].active');
      if (activeWord) {
        activeWord.forEach(word => word.classList.remove('active'));
      }
      this.$nextTick(() => this.hideEmptySpan());
    },

    removeQlTagFormatting() {
      this.quill.formatText(0, this.quill.getLength(), {
        'active-tag': false,
        ...(!this.trainerTags.length && { 'ql-tag': false })
      });
    },

    collapseDialog() {
      this.isCollapse = !this.isCollapse;
      this.$nextTick(() => {
        this.$emit('setDialogPosition');
      });
    },

    initGetTrainerTagOptionList() {
      if (!this.isZipanService) {
        getApiMethod('getTagOptionList')();
      }
    }
  },
  beforeDestroy() {
    this.qlTag.removeEventListener('resize', this.showTagPopup);
    this.qlTag.removeEventListener('mousedown', this.addTextBgSelection);
  }
};
</script>

<style scoped src="@/assets/css/pc/trainer_tag.css"></style>
